// Storefront editor schema — the single source of truth for "what can a
// merchant customise, where does it live on the page, and where is it stored".
//
// Both editing surfaces render from this file:
//   • the live editor overlay (apps/storefront) — spatial: click the page
//   • the dashboard theme page (apps/erp-web)   — catalog: browse by group
//
// Before this existed the two surfaces hand-mirrored each other's field lists
// and drifted (different labels, different caps, fields present in one and
// missing from the other). A section/field declared here is, by construction,
// reachable from both.
//
// Pure TypeScript: no React, no I/O, no data-access imports beyond types.

import type { HomeSectionConfig } from '@erp/data-access/interfaces';
import type { LocalizedText } from './theme-settings';

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

// --------------------------------------------------------------- Storage

/**
 * Where a field's value is persisted.
 *  - `settings` → store_settings column (UpdateStoreSettingsInput)
 *  - `theme`    → store_theme column (UpdateStoreThemeInput)
 *  - `bag`      → the active theme's JSONB bag (pebble/elessi/woodmartSettings)
 *  - `banner`   → a store_banners row (the section resolves which one)
 */
export type EditorSurface = 'settings' | 'theme' | 'bag' | 'banner';

/**
 * How a field is rendered AND how the click-to-edit layer treats the node.
 * `localized*` kinds carry a FR/AR/EN triplet and must never be written with a
 * single string fanned out to all three locales — that silently destroys the
 * translations a merchant entered in the other languages.
 */
export type EditorFieldKind =
  | 'text'
  | 'textarea'
  | 'localized'
  | 'localizedMultiline'
  | 'color'
  | 'image'
  | 'toggle'
  | 'select'
  | 'number'
  | 'product'
  | 'products'
  | 'category'
  /** Map each of the shop's categories to an icon. One control, because the
   *  rows are the tenant's categories — not a list the merchant adds to. */
  | 'categoryIcons'
  | 'list';

/** A translatable label carried by the schema so the editors need no i18n lookup. */
export interface EditorLabel {
  fr: string;
  ar: string;
  en: string;
}

/**
 * What a merchant should hand their designer for one image slot.
 *
 * `width`/`height` are the RECOMMENDED EXPORT size in pixels — roughly twice
 * the largest box the storefront paints the image into, so it stays sharp on a
 * retina screen — not the CSS box itself. The ratio between them is what
 * matters most: every slot below is `object-fit: cover`, so an image of the
 * wrong shape is not scaled down, it is cropped, and the merchant discovers
 * which half of their photo survived only after publishing.
 */
export interface EditorMediaSpec {
  width: number;
  height: number;
  /** `cover` crops to fill (the default); `contain` fits the whole image in. */
  fit: 'cover' | 'contain';
  /** Anything the ratio alone does not say — safe areas, transparency, text. */
  note?: EditorLabel;
}

/** Human ratio label for a spec: 1600×900 → "16:9". */
export function mediaRatioLabel(spec: EditorMediaSpec): string {
  const divisor = greatestCommonDivisor(spec.width, spec.height);
  return `${Math.round(spec.width / divisor)}:${Math.round(spec.height / divisor)}`;
}

function greatestCommonDivisor(a: number, b: number): number {
  return b === 0 ? a : greatestCommonDivisor(b, a % b);
}

/** How far a real file may drift from the spec before the editor speaks up. */
export const MEDIA_RATIO_TOLERANCE = 0.06;
export const MEDIA_MIN_WIDTH_RATIO = 0.6;

export type MediaCheck =
  | { status: 'ok' }
  | { status: 'ratio'; expected: string; actual: string }
  | { status: 'small'; expected: string; actual: string };

/**
 * Compare a chosen file against its slot. Deliberately advisory: a merchant
 * with only one photo of a product must still be able to use it, so the caller
 * warns and offers to continue rather than refusing the upload.
 *
 * The ratio check applies to `cover` slots ONLY. On a `contain` slot the whole
 * image stays visible whatever its shape — the spec's ratio is the shape of the
 * BOX, i.e. a ceiling, not an obligation. Every `contain` slot in the schema is
 * a logo or a favicon, and logos are arbitrary shapes: a square mark dropped
 * into the 360 × 88 header slot is perfectly correct, yet drifts 75 % from the
 * spec. Warning there would fire on almost every real upload, and the warning
 * itself would be false — it promises the edges will be cropped, and with
 * `object-contain` nothing is ever cropped. Only the sharpness check survives.
 */
export function checkMedia(spec: EditorMediaSpec | undefined, width: number, height: number): MediaCheck {
  if (!spec || width <= 0 || height <= 0) return { status: 'ok' };
  const actual = `${width} × ${height}`;
  const expected = `${spec.width} × ${spec.height}`;

  if (spec.fit === 'cover') {
    const drift = Math.abs(width / height - spec.width / spec.height) / (spec.width / spec.height);
    if (drift > MEDIA_RATIO_TOLERANCE) return { status: 'ratio', expected, actual };
  }

  // Blurriness is about how far the browser has to ENLARGE the file, which
  // depends on how the box uses it: `cover` scales until the box is full, so
  // the tighter dimension wins; `contain` stops as soon as the image fits, so
  // the looser one does. Comparing widths alone — as this used to — called a
  // tall logo "too small" when it was about to be scaled DOWN.
  const scale =
    spec.fit === 'cover'
      ? Math.max(spec.width / width, spec.height / height)
      : Math.min(spec.width / width, spec.height / height);
  if (scale > 1 / MEDIA_MIN_WIDTH_RATIO) return { status: 'small', expected, actual };
  return { status: 'ok' };
}

export interface EditorFieldDef {
  /**
   * Stable identifier, also the value of `data-sf-field` on the storefront
   * node that renders this value. Format: `<sectionKey>.<name>`.
   */
  path: string;
  kind: EditorFieldKind;
  surface: EditorSurface;
  /**
   * `settings` / `theme`: the column base name WITHOUT the locale suffix
   * (`storeTitle` → storeTitleFr/Ar/En) for localized kinds, or the exact
   * column name otherwise.
   * `bag`: dot path inside the resolved bag (`story.heading`).
   * `banner`: the banner column base name (`title`, `description`, `ctaText`).
   */
  key: string;
  label: EditorLabel;
  /** One-line explanation shown under the control. */
  hint?: EditorLabel;
  /** `select` only. */
  options?: Array<{ value: string; label: EditorLabel }>;
  /** `number` only. */
  min?: number;
  max?: number;
  step?: number;
  /** `products` / `list` only — the cap the STOREFRONT actually renders. */
  max_items?: number;
  /** `image` only — the size the merchant should export for this slot. */
  media?: EditorMediaSpec;
  /**
   * True when the storefront falls back to built-in copy if the field is
   * empty, so the editors can show "leave empty for the default" instead of
   * an alarming blank.
   */
  fallsBackToDefault?: boolean;
}

/** Where a section sits in the editor's navigation tree. */
export type EditorSectionGroup =
  /** Always-present chrome above the page content (announcement, header). */
  | 'header'
  /** Reorderable homepage blocks — mirrors home-section-defs per theme. */
  | 'home'
  /** Always-present chrome below the page content (footer, floating buttons). */
  | 'footer'
  /** Store-wide settings with no single place on the page. */
  | 'global';

/** Icon name resolved to an SVG by each surface's icon map. */
export type EditorIconName =
  | 'announcement'
  | 'header'
  | 'slideshow'
  | 'grid'
  | 'product'
  | 'story'
  | 'marquee'
  | 'image'
  | 'sparkle'
  | 'quote'
  | 'shield'
  | 'mail'
  | 'footer'
  | 'social'
  | 'whatsapp'
  | 'palette'
  | 'type'
  | 'brand'
  | 'tag'
  | 'deal'
  | 'list';

export interface EditorSectionDef {
  /**
   * For `home` sections this is the home-section key (also the value of
   * `data-sf-region` emitted by every theme's homepage renderer), so the tree,
   * the page and the stored order all line up. For chrome/global sections it
   * is a stable synthetic key.
   */
  key: string;
  group: EditorSectionGroup;
  label: EditorLabel;
  /** Shown under the label in the tree. */
  description?: EditorLabel;
  icon: EditorIconName;
  /** Chrome and global sections cannot be reordered or hidden from the tree. */
  pinned?: boolean;
  /**
   * The settings/theme field that toggles the whole section on the storefront,
   * when one exists (e.g. `trustEnabled`). Rendered as the eye control.
   */
  visibilityField?: { surface: 'settings' | 'theme'; key: string };
  fields: EditorFieldDef[];
  /**
   * Set when the section's richest content lives in a bespoke panel that the
   * generic field renderer cannot express (repeatable nested lists such as the
   * Pebble scroll-story phrases or the testimonial cards). The surfaces render
   * `fields` first, then mount the named panel underneath.
   */
  richPanel?: string;
}

// --------------------------------------------------------------- Label helper

/** Read a schema label in the editor's chrome language, French-first. */
export function editorLabel(label: EditorLabel | undefined, lang: 'fr' | 'ar' | 'en'): string {
  if (!label) return '';
  return label[lang] || label.fr || label.en || label.ar || '';
}

// --------------------------------------------------------------- Section order

/**
 * Move a homepage section from one index to another, returning a new array.
 * Shared by the live editor's drag-and-drop tree and the dashboard so both
 * produce byte-identical `homepage_sections` payloads.
 *
 * Out-of-range indices are clamped; a no-op move returns the original array
 * reference so React can skip the re-render.
 */
export function moveHomeSection(
  sections: readonly HomeSectionConfig[],
  from: number,
  to: number,
): HomeSectionConfig[] {
  const list = [...sections];
  if (list.length === 0) return list;
  const source = Math.min(Math.max(from, 0), list.length - 1);
  const target = Math.min(Math.max(to, 0), list.length - 1);
  if (source === target) return list;
  const [moved] = list.splice(source, 1);
  if (!moved) return list;
  list.splice(target, 0, moved);
  return list;
}

/** Flip one section's visibility, returning a new array. */
export function toggleHomeSection(
  sections: readonly HomeSectionConfig[],
  key: string,
): HomeSectionConfig[] {
  return sections.map((section) =>
    section.key === key ? { key: section.key, visible: !section.visible } : section,
  );
}

// --------------------------------------------------------------- Style options

/**
 * The typography / shape option lists. Previously duplicated as
 * "mirrors of ThemePage's option lists" inside the live editor — one list, one
 * place, so a new font can never appear in only one of the two surfaces.
 */
export const LATIN_FONT_OPTIONS = ['Inter', 'Roboto', 'Poppins', 'Montserrat', 'Open Sans', 'Lato', 'Rubik'] as const;
export const ARABIC_FONT_OPTIONS = ['Cairo', 'Tajawal', 'IBM Plex Arabic', 'Rubik'] as const;

export const HEADING_SCALE_OPTIONS: Array<{ value: string; label: EditorLabel }> = [
  { value: 'soft', label: { fr: 'Douce', ar: 'ناعمة', en: 'Soft' } },
  { value: 'default', label: { fr: 'Normale', ar: 'عادية', en: 'Normal' } },
  { value: 'bold', label: { fr: 'Marquée', ar: 'بارزة', en: 'Bold' } },
];

export const BORDER_RADIUS_OPTIONS: Array<{ value: string; label: EditorLabel }> = [
  { value: '0', label: { fr: 'Carré', ar: 'مربع', en: 'Square' } },
  { value: '0.25rem', label: { fr: 'Très arrondi léger', ar: 'انحناء خفيف', en: 'Extra small' } },
  { value: '0.5rem', label: { fr: 'Arrondi', ar: 'مستدير', en: 'Rounded' } },
  { value: '0.75rem', label: { fr: 'Très arrondi', ar: 'مستدير جدًا', en: 'Large' } },
  { value: '1rem', label: { fr: 'Doux', ar: 'ناعم', en: 'Extra large' } },
  { value: '9999px', label: { fr: 'Pilule', ar: 'كبسولة', en: 'Pill' } },
];

export const SHADOW_STYLE_OPTIONS: Array<{ value: string; label: EditorLabel }> = [
  { value: 'none', label: { fr: 'Aucune', ar: 'بدون', en: 'None' } },
  { value: 'soft', label: { fr: 'Douce', ar: 'خفيف', en: 'Soft' } },
  { value: 'default', label: { fr: 'Normale', ar: 'عادي', en: 'Normal' } },
  { value: 'bold', label: { fr: 'Marquée', ar: 'قوي', en: 'Bold' } },
];

// --------------------------------------------------------------- Theme bags

/** Read a dot path (`story.heading`) out of a resolved theme bag. */
export function readBagPath(bag: unknown, path: string): unknown {
  let current: unknown = bag;
  for (const segment of path.split('.')) {
    if (!isRecord(current)) return undefined;
    current = current[segment];
  }
  return current;
}

/**
 * Write a dot path into a theme bag, returning a NEW bag.
 *
 * Crucially this operates on the RAW stored bag, not the resolved one. The old
 * panels handed back the fully-resolved bag on every keystroke, so the first
 * edit to (say) the marquee froze every Pebble default — scroll-story phrases,
 * testimonials, the story block — into the tenant's row, where they stopped
 * tracking later default and translation changes. Writing only the touched path
 * keeps everything else absent, and therefore still defaulted.
 */
export function writeBagPath(
  bag: Record<string, unknown> | null | undefined,
  path: string,
  value: unknown,
): Record<string, unknown> {
  const segments = path.split('.');
  const root: Record<string, unknown> = isRecord(bag) ? { ...bag } : {};
  let cursor = root;
  for (let index = 0; index < segments.length - 1; index += 1) {
    const segment = segments[index];
    if (segment === undefined) break;
    const existing = cursor[segment];
    const next: Record<string, unknown> = isRecord(existing) ? { ...existing } : {};
    cursor[segment] = next;
    cursor = next;
  }
  const leaf = segments[segments.length - 1];
  if (leaf !== undefined) cursor[leaf] = value;
  return root;
}

/**
 * Merge one locale into a localized value held in a bag, preserving the other
 * two. `undefined` and non-object stored values start a fresh triplet.
 */
export function writeBagLocale(
  current: unknown,
  lang: 'fr' | 'ar' | 'en',
  value: string,
): LocalizedText {
  const stored = isRecord(current) ? current : null;
  const pick = (key: 'fr' | 'ar' | 'en'): string | null => {
    const value = stored?.[key];
    return typeof value === 'string' ? value : null;
  };
  const base: LocalizedText = stored ? { fr: pick('fr'), ar: pick('ar'), en: pick('en') } : {};
  return { ...base, [lang]: value };
}

/** The store_theme column holding a theme's bag. */
export function bagColumnFor(theme: string | null | undefined): 'pebbleSettings' | 'elessiSettings' | 'woodmartSettings' {
  if (theme === 'pebble') return 'pebbleSettings';
  if (theme === 'elessi') return 'elessiSettings';
  return 'woodmartSettings';
}

// --------------------------------------------------------------- Field lookup

/** Index every field of a section list by its `data-sf-field` path. */
export function indexEditorFields(sections: readonly EditorSectionDef[]): Map<string, { section: EditorSectionDef; field: EditorFieldDef }> {
  const index = new Map<string, { section: EditorSectionDef; field: EditorFieldDef }>();
  for (const section of sections) {
    for (const field of section.fields) {
      index.set(field.path, { section, field });
    }
  }
  return index;
}

/** Resolve the section a `data-sf-field` / `data-sf-region` value belongs to. */
export function findEditorSection(
  sections: readonly EditorSectionDef[],
  key: string,
): EditorSectionDef | undefined {
  const direct = sections.find((section) => section.key === key);
  if (direct) return direct;
  // `hero.storeTitle` → `hero`
  const sectionKey = key.split('.')[0];
  return sections.find((section) => section.key === sectionKey);
}
